import moment from "moment";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch {
	jsPDF = null;
}

const T = {
	primary: [16, 89, 191],
	dark: [11, 31, 66],
	bg: [245, 247, 250],
	text: [30, 41, 59],
	muted: [100, 116, 139],
	white: [255, 255, 255],
	success: [22, 163, 74],
	warn: [217, 119, 6],
};

export const generatePresentationPDF = () => {
	if (!jsPDF) throw new Error("jsPDF non disponibile");

	const doc = new jsPDF({orientation: "landscape", unit: "pt", format: "a4"});
	const W = doc.internal.pageSize.getWidth();
	const H = doc.internal.pageSize.getHeight();

	const setC = (c) => doc.setTextColor(c[0], c[1], c[2]);
	const setF = (c) => doc.setFillColor(c[0], c[1], c[2]);

	let slideNum = 0;

	const newSlide = () => {
		if (slideNum > 0) doc.addPage();
		slideNum++;
	};

	// ── Slide types ───────────────────────────────────────────────────────────

	const coverSlide = (title, subtitle, tagline) => {
		newSlide();
		setF(T.dark);
		doc.rect(0, 0, W, H, "F");

		// Accent line
		setF(T.primary);
		doc.rect(0, H * 0.48, W, 4, "F");

		doc.setFont("Helvetica", "bold");
		doc.setFontSize(42);
		setC(T.white);
		doc.text(title, W / 2, H * 0.38, {align: "center"});

		doc.setFont("Helvetica", "normal");
		doc.setFontSize(18);
		doc.setTextColor(180, 190, 220);
		doc.text(subtitle, W / 2, H * 0.56, {align: "center"});

		if (tagline) {
			doc.setFontSize(11);
			doc.setTextColor(120, 135, 160);
			doc.text(tagline, W / 2, H * 0.65, {align: "center"});
		}
	};

	const sectionSlide = (title, subtitle) => {
		newSlide();
		setF(T.primary);
		doc.rect(0, 0, W, H, "F");

		doc.setFont("Helvetica", "bold");
		doc.setFontSize(30);
		setC(T.white);
		doc.text(title, W / 2, H / 2 - 10, {align: "center"});

		if (subtitle) {
			doc.setFont("Helvetica", "normal");
			doc.setFontSize(14);
			doc.setTextColor(200, 210, 230);
			doc.text(subtitle, W / 2, H / 2 + 20, {align: "center"});
		}
	};

	const contentSlide = (title, leftItems, rightItems) => {
		newSlide();
		setF(T.white);
		doc.rect(0, 0, W, H, "F");

		// Title bar
		setF(T.dark);
		doc.rect(0, 0, W, 56, "F");
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(18);
		setC(T.white);
		doc.text(title, 44, 36);

		const hasRight = rightItems && rightItems.length > 0;
		const colW = hasRight ? (W - 120) / 2 : W - 90;

		const renderColumn = (items, startX, startY) => {
			let y = startY;
			items.forEach((item) => {
				if (item.startsWith("##")) {
					doc.setFont("Helvetica", "bold");
					doc.setFontSize(13);
					setC(T.primary);
					doc.text(item.replace("## ", ""), startX, y);
					y += 6;
					setF(T.primary);
					doc.rect(startX, y, 28, 1.5, "F");
					y += 14;
				} else if (item.startsWith("→")) {
					doc.setFont("Helvetica", "bold");
					doc.setFontSize(11);
					setC(T.primary);
					doc.text(item, startX + 4, y);
					y += 16;
				} else {
					doc.setFont("Helvetica", "normal");
					doc.setFontSize(11.5);
					setC(T.text);
					const lines = doc.splitTextToSize("•  " + item, colW - 8);
					lines.forEach((line) => {
						doc.text(line, startX + 6, y);
						y += 15;
					});
				}
			});
		};

		renderColumn(leftItems, 44, 82);
		if (hasRight) renderColumn(rightItems, W / 2 + 16, 82);
	};

	const kpiSlide = (title, kpis) => {
		newSlide();
		setF(T.white);
		doc.rect(0, 0, W, H, "F");

		setF(T.dark);
		doc.rect(0, 0, W, 56, "F");
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(18);
		setC(T.white);
		doc.text(title, 44, 36);

		const cardW = (W - 88 - (kpis.length - 1) * 14) / kpis.length;
		const cardH = 100;
		const startY = (H - 56 - cardH) / 2 + 56;

		kpis.forEach((kpi, i) => {
			const x = 44 + i * (cardW + 14);
			setF(T.bg);
			doc.roundedRect(x, startY, cardW, cardH, 8, 8, "F");

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(28);
			setC(T.primary);
			doc.text(kpi.value, x + cardW / 2, startY + 38, {align: "center"});

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(9);
			setC(T.muted);
			doc.text(kpi.label.toUpperCase(), x + cardW / 2, startY + 58, {align: "center"});

			if (kpi.sub) {
				doc.setFont("Helvetica", "normal");
				doc.setFontSize(9);
				setC(T.muted);
				const lines = doc.splitTextToSize(kpi.sub, cardW - 16);
				lines.forEach((line, li) => {
					doc.text(line, x + cardW / 2, startY + 74 + li * 12, {align: "center"});
				});
			}
		});
	};

	// ══════════════════════════════════════════════════════════════════════════
	// SLIDES
	// ══════════════════════════════════════════════════════════════════════════

	// ── 1. Cover ──────────────────────────────────────────────────────────────

	coverSlide(
		"FOOTBALL DECISION OS",
		"Il sistema operativo decisionale per settori giovanili e academy",
		"Dalla raccolta dati alla decisione. Un'unica piattaforma.",
	);

	// ── 2. Visione ────────────────────────────────────────────────────────────

	contentSlide("La visione", [
		"## Il calcio giovanile ha un problema di dati",
		"I club raccolgono informazioni da decine di fonti: presenze, RPE, GPS, video, valutazioni, medico, segreteria",
		"Ma questi dati restano in fogli Excel, WhatsApp, app diverse che non comunicano",
		"Il DS, l'allenatore e la dirigenza non hanno mai il quadro completo",
		"Le decisioni vengono prese a sensazione invece che su dati oggettivi",
	], [
		"## Football Decision OS risolve questo",
		"Un'unica piattaforma dove convergono TUTTI i dati operativi del club",
		"Un layer di intelligenza che li incrocia e genera alert, indici, raccomandazioni",
		"Report automatici professionali con un click",
		"Ogni ruolo vede cio di cui ha bisogno: dal DS all'allenatore, dal medico al genitore",
	]);

	// ── 3. KPI piattaforma ────────────────────────────────────────────────────

	kpiSlide("La piattaforma in numeri", [
		{value: "8", label: "Ruoli utente", sub: "DS, Manager, Coach, Analyst, Player, Scout, Genitore, Admin"},
		{value: "50+", label: "Feature", sub: "Gestione, intelligence, report, analytics"},
		{value: "19", label: "Attributi SMART", sub: "Valutazione scientifica per ruolo"},
		{value: "7", label: "Playstyle", sub: "Rilevamento automatico archetipo giocatore"},
		{value: "∞", label: "Rose", sub: "Multi-squadra: da U10 a Prima Squadra"},
	]);

	// ── 4. Section: Gestione operativa ────────────────────────────────────────

	sectionSlide("Gestione Operativa", "Tutto quello che serve per far funzionare il club, in un unico posto");

	// ── 5. Rose e giocatori ───────────────────────────────────────────────────

	contentSlide("Rose, Giocatori e Staff", [
		"## Gestione multi-squadra per stagione",
		"Rose illimitate per stagione: U15, U17, Primavera, Prima Squadra",
		"Ogni rosa con giocatori, staff, moduli tattici",
		"Campo interattivo con drag-and-drop per formazioni",
		"Profilo giocatore completo: anagrafica, contratto, dati antropometrici, curve di crescita",
		"Confronto con la coorte: come si posiziona rispetto ai compagni dello stesso anno",
	], [
		"## Contratti e segreteria",
		"Gestione contratti con alert scadenze automatici",
		"Quote di iscrizione con rateizzazione e tracking pagamenti",
		"Gestione documentale: certificato medico, carta identita, privacy",
		"Alert automatici per documenti in scadenza",
		"Dashboard segreteria con KPI incasso e stato documentale",
	]);

	// ── 6. Attivita ───────────────────────────────────────────────────────────

	contentSlide("Partite e Allenamenti", [
		"## Partite",
		"Creazione con club casa/ospite e rosa associata",
		"Collegamento a competizione, girone, giornata (opzionale per amichevoli)",
		"Convocazioni, distinta titolari/riserve, eventi (gol, cartellini, sostituzioni)",
		"Stats avanzate per giocatore: rating, duelli, intercetti, tiri, dribbling",
		"Report gara portiere con shot map interattiva (origine tiro + arrivo in porta)",
		"Video embed: incolla un link YouTube e guarda il video nella pagina partita",
	], [
		"## Allenamenti",
		"Presenze con aggiunta inline (senza modal) da rosa + altre rose",
		"RPE per ogni giocatore",
		"Esercizi con fasi, durata, descrizione",
		"Prossimita match: quanti giorni dall'ultima partita e dalla prossima",
		"## Calendario unificato",
		"Vista mese/settimana con partite e allenamenti insieme",
		"Filtro per rosa",
		"Click su un giorno per creare allenamento o partita",
	]);

	// ── 7. Strumenti per l'allenatore ─────────────────────────────────────────

	contentSlide("Strumenti per l'allenatore", [
		"## Match Day",
		"Una pagina unica pre-partita con tutto il necessario:",
		"Prossima partita con countdown",
		"Readiness di ogni giocatore (TQR + RPE + infortuni)",
		"Campo tattico interattivo con drag-and-drop",
		"Ultime 5 partite con risultati (W/D/L)",
		"Grafici coach: carico settimanale, distribuzione minuti, presenze allenamento",
	], [
		"## Lavagna tattica",
		"Campo da calcio orizzontale completamente interattivo",
		"15 strumenti: frecce, linee, curve, disegno libero",
		"Elementi da campo: coni, paletti, scalette, porticine, palloni",
		"Giocatori e avversari numerati con drag-and-drop",
		"7 colori, spessore regolabile, undo illimitato",
		"Esporta come immagine PNG",
		"Salvataggio automatico: riapri e trovi tutto come l'hai lasciato",
	]);

	// ── 8. Section: Intelligence Layer ────────────────────────────────────────

	sectionSlide("Intelligence Layer", "Il cuore del sistema: dai dati alle decisioni");

	// ── 9. Dashboard e Brief ──────────────────────────────────────────────────

	contentSlide("Dashboard Decisionale e Decision Brief", [
		"## Dashboard del Direttore Sportivo",
		"KPI in tempo reale: rosa, prossima partita, TQR/RPE medi, infortunati",
		"Alert automatici: TQR basso, RPE pre-partita, contratti in scadenza, rientri infortunio",
		"Squad Balance: griglia ruoli × anni di nascita per identificare carenze",
		"Lista giocatori con semaforo stato forma",
		"Toggle Panoramica / Pianificazione contratti",
	], [
		"## Decision Brief settimanale",
		"Il morning briefing del DS in una pagina:",
		"KPI globali cross-roster",
		"Smart Alerts cross-modulo con raccomandazione operativa",
		"Stato per rosa: TQR/RPE + partite + allenamenti della settimana",
		"Contratti in scadenza (30/90/180 giorni)",
		"Infortuni attivi con previsione rientro",
		"Esportabile in PDF con un click",
	]);

	// ── 10. Smart Alerts ──────────────────────────────────────────────────────

	contentSlide("Smart Alerts: il sistema che pensa per te", [
		"## Alert cross-modulo con raccomandazione",
		"Il sistema incrocia automaticamente dati da 7 moduli diversi:",
		"→ Rischio infortunio: TQR in calo + RPE in salita + storico infortuni",
		"→ Contratto + performance: scadenza imminente + giocatore performante = rinnova subito",
		"→ Talento emergente: eta formativa + rating alto + presenze crescenti",
		"→ Giocatore sottoutilizzato: buona forma ma poche presenze",
		"→ Disponibilita critica: infortuni frequenti + contratto in scadenza",
	], [
		"## Ogni alert include:",
		"Priorita (critico / alto / medio / basso)",
		"Nome giocatore e contesto",
		"Messaggio descrittivo con i dati incrociati",
		"Raccomandazione operativa: cosa fare",
		"## Esempio",
		"\"Rossi: TQR 4.8 (← 6.2), RPE 7.5 (← 5.1), 2 infortuni in 6 mesi.\"",
		"→ Ridurre il carico. Valutare riposo preventivo.",
	]);

	// ── 11. Indici giocatore ──────────────────────────────────────────────────

	contentSlide("Indici proprietari per giocatore", [
		"## 4 indici calcolati automaticamente dai dati reali",
		"→ Readiness (0-100): prontezza a giocare, da TQR + RPE + stato infortunio",
		"→ Performance (0-100): rendimento per ruolo, da report gara e stats",
		"→ Availability (%): disponibilita stagionale, da giorni persi per infortunio",
		"→ Valuation Trend: andamento delle valutazioni nel tempo",
	], [
		"## Performance differenziata per ruolo",
		"Portieri: indice parate, distribuzione, controllo aereo, palle inattive, errori",
		"Difensori: duelli, intercetti, recuperi, passaggi chiave",
		"Centrocampisti: passaggi chiave, assist, recuperi, duelli",
		"Attaccanti: gol, tiri in porta, dribbling, assist",
		"## Tutto nel profilo giocatore",
		"Cerchi colorati con score e breakdown dettagliato",
		"Visibili anche nel Player Portfolio pubblico",
	]);

	// ── 12. Valutazione SMART ─────────────────────────────────────────────────

	contentSlide("Sistema di Valutazione SMART", [
		"## 19 attributi scientifici, 5 categorie",
		"Mentale: decision making, concentrazione, mentalita, team play",
		"Tecnica: controllo, passaggio, dribbling, finalizzazione",
		"Fisico: velocita, esplosivita, resistenza, forza",
		"Tattica: posizionamento, lettura gioco, movimento senza palla, visione",
		"Duelli: offensivi, difensivi, aerei",
	], [
		"## Pesi per ruolo",
		"Ogni attributo ha un peso diverso per ATT / CC / DIF (sommano 100)",
		"Overall calcolato automaticamente per ogni ruolo",
		"## 7 playstyle rilevati automaticamente",
		"Finisher, Creator, Ball Winner, Box to Box, Explosive Winger, Target Man, Deep Playmaker",
		"## Radar chart a 5 aree",
		"Preview live durante la compilazione",
		"Visibile nel profilo giocatore, nel portfolio pubblico e nel report PDF",
	]);

	// ── 13. Section: Report e condivisione ────────────────────────────────────

	sectionSlide("Report e Condivisione", "Dati che escono dalla piattaforma e vanno in riunione");

	// ── 14. Report PDF ────────────────────────────────────────────────────────

	contentSlide("Report PDF automatici", [
		"## Intelligence Report Giocatore",
		"Generato con un click dalla pagina giocatore:",
		"Profilo completo (anagrafica, contratto, fisico)",
		"Stats stagionali (da dati reali + provider esterni)",
		"Profilo SMART con radar e playstyle",
		"Storico infortuni con giorni persi",
		"Valutazioni aggregate",
		"Raccomandazione algoritmica: RINNOVARE / MONITORARE / CEDERE",
	], [
		"## Altri report",
		"Season Report: analisi completa della squadra a fine stagione",
		"Decision Brief PDF: il briefing settimanale stampabile",
		"Report partita: distinta, eventi, PDF ufficiale",
		"Report allenamento: presenze, RPE, esercizi",
		"Report gara portiere: shot map, 6 KPI calcolati, PDF professionale",
		"## Player Portfolio",
		"Link pubblico condivisibile per scout, agenti, genitori",
		"Profilo + stats + radar SMART + crescita + valutazioni",
	]);

	// ── 15. Section: Video Intelligence ───────────────────────────────────────

	sectionSlide("Video Intelligence", "Dal video alle azioni: tagging, clip, analisi");

	// ── 16. Video ─────────────────────────────────────────────────────────────

	contentSlide("Analisi Video", [
		"## Video integrato nelle partite",
		"Incolla un link YouTube/Vimeo nella partita",
		"Il video si vede embedded nella pagina",
		"L'allenatore guarda il video mentre compila il report",
		"## Video tagging",
		"Timeline dei momenti importanti sotto il video",
		"10 tipi di azione: gol, tiro, assist, fallo, corner, azione pericolosa, sostituzione, parata, nota tattica",
		"Associazione giocatore e note per ogni tag",
		"Click sul tag: il video salta a quel momento",
	], [
		"## Shot Map interattiva (portieri)",
		"Porta da calcio cliccabile con meta campo",
		"Segna il punto di origine del tiro (da dove tira) e il punto di arrivo (sulla porta)",
		"3 tipi: parata (verde), gol subito (rosso), fuori/palo (giallo)",
		"Linee tratteggiate collegano origine e arrivo",
		"Stats automatiche: tiri, parate, gol, % parate",
		"## Evoluzione futura",
		"Tagging automatico AI dal video",
		"Integrazione con videocamere intelligenti per import automatico dati",
	]);

	// ── 17. Section: Area medica e performance ────────────────────────────────

	sectionSlide("Performance e Area Medica", "Monitorare, prevenire, decidere");

	// ── 18. Performance ───────────────────────────────────────────────────────

	contentSlide("Wellness e Performance", [
		"## Wellness Monitor",
		"TQR (Total Quality Recovery): stato di recupero giocatore, scala 6-20",
		"RPE (Rate of Perceived Exertion): fatica percepita, scala 1-10",
		"VAS (Visual Analog Scale): dolore percepito per zona corporea",
		"Dashboard con trend settimanali e mensili",
	], [
		"## Dati GPS",
		"Import sessioni GPS",
		"Distanza, velocita, accelerazione, sprint per giocatore",
		"## Test atletici",
		"Tracciamento test fisici nel tempo",
		"Confronto tra giocatori e con la media",
		"## Profilo di sviluppo",
		"Curve antropometriche (altezza/peso nel tempo)",
		"Confronto con la coorte dello stesso anno di nascita",
		"Velocita di crescita calcolata automaticamente",
	]);

	// ── 19. Area medica ───────────────────────────────────────────────────────

	contentSlide("Area Medica", [
		"## Cartelle mediche",
		"Una cartella per ogni giocatore",
		"Storico infortuni con diagnosi, parte corpo, date, giorni persi",
		"Distinzione infortunio / malattia",
		"Data rientro prevista con alert automatico nel Decision Brief",
	], [
		"## Integrazione con il decisionale",
		"Gli infortuni attivi appaiono nella Dashboard del DS",
		"I giocatori infortunati hanno badge visivo ovunque",
		"Il Readiness Index incorpora lo stato infortunio",
		"Gli Smart Alerts incrociano storico infortuni con carico e contratto",
		"La disponibilita stagionale (Availability Index) conta i giorni persi",
		"→ Il DS sa sempre chi e disponibile e chi e a rischio",
	]);

	// ── 20. Section: Per chi e ────────────────────────────────────────────────

	sectionSlide("Per chi e pensato", "Dal settore giovanile alla prima squadra");

	// ── 21. Target ────────────────────────────────────────────────────────────

	contentSlide("I nostri utenti", [
		"## Settori giovanili strutturati",
		"6-10 squadre da gestire contemporaneamente",
		"Il DS ha il quadro completo di tutte le categorie",
		"Confronto coorte per decisioni su rinnovi a 16 anni",
		"## Academy e affiliate",
		"Il club padre monitora i talenti emergenti dalle affiliate",
		"Smart Alerts segnalano chi e pronto per la promozione",
		"Portfolio pubblico per far emergere i giovani",
	], [
		"## Club dilettantistici",
		"Segreteria integrata: sostituisce Golee",
		"Lavagna tattica: sostituisce YouCoach",
		"Match Day: tutto per la preparazione della partita",
		"## Ogni ruolo vede cio di cui ha bisogno",
		"DS: Dashboard + Brief + Report + Contratti",
		"Allenatore: Match Day + Lavagna + Presenze + Video",
		"Giocatore: Profilo + RPE + TQR + Partite",
		"Genitore: Calendario + Pagamenti + Documenti (in sviluppo)",
	]);

	// ── 22. Chiusura ──────────────────────────────────────────────────────────

	coverSlide(
		"FOOTBALL DECISION OS",
		"Dalla raccolta dati alla decisione.\nDal campo alla scrivania del DS.\nTutto in un'unica piattaforma.",
		"www.footballdecisionos.com",
	);

	// ── Footer ────────────────────────────────────────────────────────────────

	const totalPages = doc.internal.getNumberOfPages();
	for (let p = 1; p <= totalPages; p++) {
		doc.setPage(p);
		doc.setFont("Helvetica", "normal");
		doc.setFontSize(7);
		setC(T.muted);
		doc.text("Football Decision OS", 40, H - 16);
		doc.text(p + " / " + totalPages, W - 40, H - 16);
	}

	doc.save(`Football_Decision_OS_${moment().format("YYYY-MM-DD")}.pdf`);
};
